// src/pages/users/tabs/ProfileTab.jsx
import React, { useEffect, useState } from 'react';
import {
  FiMail, FiPhone, FiDownload, FiExternalLink, FiFileText, FiImage, FiClock, FiUser
} from 'react-icons/fi';
import { mediaUrl } from '../../../utils/media';

function StatusBadge({ value }) {
  if (!value) return <span>—</span>;
  const good = String(value).toLowerCase() === 'active';
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold
        ${good ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'}`}
    >
      {String(value)}
    </span>
  );
}

function Row({ left, center, right }) {
  return (
    <tr className="border-t border-gray-800">
      <td className="px-4 py-3 text-gray-300">{left}</td>
      <td className="px-4 py-3 text-white">{center}</td>
      <td className="px-4 py-3 text-white">{right}</td>
    </tr>
  );
}

const isImage = (url = '') => /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(String(url));
const isPdf   = (url = '') => /\.pdf$/i.test(String(url));

export default function ProfileTab({ user }) {
  // Profile picture (what the user sees as their avatar)
  const profilePhotoUrl =
    user?.profile?.avatarUrl ||
    user?.verifiedPortraitPath ||
    user?.avatarUrl ||
    user?.photoUrl ||
    user?.selfieImagePath ||
    user?.profileImage ||
    null;

  // Verified face (exact selfie that passed) + timestamp
  const verifiedFaceUrl = user?.verifiedPortraitPath || null;
  const verifiedFaceAt  = user?.lastVerifiedSelfieAt || null;

  // Verified OCR doc image
  const verifiedDoc = user?.verifiedDocImagePath
    ? {
        name: user?.verifiedDocType
          ? `Verified ${cap(user.verifiedDocType)} Image`
          : 'Verified Document Image',
        url: user.verifiedDocImagePath,
        type: 'image',
        createdAt: user?.verifiedDocAt || user?.updatedAt || null,
        docType: user?.verifiedDocType || undefined,
      }
    : null;

  // (Optional) last uploaded doc if you still want to show it
  const lastUploadDoc = user?.docImagePath
    ? {
        name: 'Document (Last Upload)',
        url: user.docImagePath,
        type: 'image',
        createdAt: user?.updatedAt || null,
      }
    : null;

  return (
    <div className="space-y-6">
      {/* BASIC ------------------------------------------------------------- */}
      <div className="bg-[#1b2433] rounded-2xl shadow-lg">
        <div className="px-6 py-4 border-b border-gray-800">
          <h3 className="text-white font-semibold">Basic Information</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <tbody>
              <Row
                left={<span className="inline-flex items-center gap-2"><FiMail /> Email</span>}
                center={user.email || '—'}
                right={<span className="inline-flex items-center gap-2"><FiPhone /> {user.phone || '—'}</span>}
              />
              <Row
                left="Status"
                center={<StatusBadge value={user.status || 'Active'} />}
                right={`Joined ${new Date(user.createdAt).toLocaleDateString()}`}
              />
            </tbody>
          </table>
        </div>
      </div>

      {/* SHIMBIRR INFO ----------------------------------------------------- */}
      <div className="bg-[#1b2433] rounded-2xl shadow-lg p-6">
        <h3 className="text-white font-semibold mb-4">Shimbirr Info</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field label="Shimbirr ID" value={user.shimbirrId || 'Pending'} />
          <Field label="Role"        value={cap(user.role)} />
          <Field label="Account"     value={cap(user.accountType || 'Standard')} />
          {user.nationalId && <Field label="National ID" value={user.nationalId} />}
          {user.dateOfBirth && <Field label="Date of Birth" value={fmtDate(user.dateOfBirth)} />}
          {user.gender && <Field label="Gender" value={cap(user.gender)} />}
          {user.createdBy && <Field label="Created By" value={user.createdBy} />}
          {user.lastLoginAt && <Field label="Last Login" value={fmtDateTime(user.lastLoginAt)} />}
        </div>
      </div>

      {/* PHOTOS (PROFILE + VERIFIED FACE) ---------------------------------- */}
      <div className="bg-[#1b2433] rounded-2xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold">Profile Photos</h3>
          {verifiedFaceAt && (
            <div className="text-xs text-gray-400 inline-flex items-center gap-1">
              <FiClock /> Verified on: {fmtDateTime(verifiedFaceAt)}
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <PhotoCard title="Profile Photo" url={profilePhotoUrl ? mediaUrl(profilePhotoUrl) : null} />
          <PhotoCard title="Verified Face" url={verifiedFaceUrl ? mediaUrl(verifiedFaceUrl) : null} />
        </div>
      </div>

      {/* DOCUMENTS & IDs (verified OCR doc) -------------------------------- */}
      <div className="bg-[#1b2433] rounded-2xl shadow-lg p-6">
        <h3 className="text-white font-semibold mb-4">Documents &amp; IDs</h3>
        {verifiedDoc ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            <DocCard doc={verifiedDoc} />
            {/* show last non-verified upload too (optional): */}
            {lastUploadDoc && <DocCard doc={lastUploadDoc} />}
          </div>
        ) : (
          <p className="text-gray-400">No verified documents.</p>
        )}
      </div>

      {/* ACTIVITY ----------------------------------------------------------- */}
      <div className="bg-[#1b2433] rounded-2xl shadow-lg p-6">
        <h3 className="text-white font-semibold mb-4">Recent Activity</h3>
        <ul className="space-y-2 text-sm">
          <li className="text-gray-300 inline-flex items-center gap-2">
            <FiUser /> Account created: <span className="text-white">{fmtDateTime(user.createdAt)}</span>
          </li>
          {user.updatedAt && (
            <li className="text-gray-300 inline-flex items-center gap-2">
              <FiClock /> Last updated: <span className="text-white">{fmtDateTime(user.updatedAt)}</span>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}

/* --------------------------- small UI helpers ---------------------------- */
function Field({ label, value }) {
  return (
    <div className="bg-gray-900 rounded-xl p-4">
      <div className="text-xs uppercase tracking-wide text-gray-400">{label}</div>
      <div className="mt-1 text-[15px] text-white break-words">{String(value || '—')}</div>
    </div>
  );
}

function PhotoCard({ title, url }) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [url]);

  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">{title}</div>
      <div className="aspect-square rounded-xl bg-gray-900 overflow-hidden flex items-center justify-center">
        {url && !failed ? (
          <img
            className="w-full h-full object-cover"
            src={url}
            alt={title}
            onError={() => setFailed(true)}
          />
        ) : (
          <div className="text-gray-500">No photo</div>
        )}
      </div>
    </div>
  );
}

function DocCard({ doc }) {
  const icon = isImage(doc.url) ? <FiImage /> : <FiFileText />;
  const label = doc.name || (isPdf(doc.url) ? 'PDF Document' : 'File');
  const href = mediaUrl(doc.url);
  return (
    <div className="bg-gray-900 rounded-xl p-4 flex items-start gap-3">
      <div className="mt-0.5 text-gray-300">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-white truncate" title={label}>{label}</div>
        <div className="text-xs text-gray-400">
          {doc.type || (isImage(doc.url) ? 'image' : isPdf(doc.url) ? 'pdf' : 'file')}
          {doc.createdAt ? ` · ${fmtDate(doc.createdAt)}` : ''}
          {doc.docType ? ` · ${doc.docType}` : ''}
        </div>
        <div className="mt-2 flex gap-2">
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-gray-800 text-white text-xs hover:bg-gray-700"
          >
            <FiExternalLink /> View
          </a>
          <a
            href={href}
            download
            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-yellow-400 text-black text-xs hover:opacity-90"
          >
            <FiDownload /> Download
          </a>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------ utilities ------------------------------- */
function cap(x) { return x ? String(x).charAt(0).toUpperCase() + String(x).slice(1) : ''; }
function fmtDate(d) { try { return new Date(d).toLocaleDateString(); } catch { return String(d || '—'); } }
function fmtDateTime(d) { try { return new Date(d).toLocaleString(); } catch { return String(d || '—'); } }
